<template>
  <div class="page-ai-share">
    <!-- 固定顶部栏 -->
    <div class="header header-fixed">
      <div class="brand-area">
        <img :src="appLogo" class="brand-logo" alt="logo" />
        <span class="brand-text">Kukukao</span>
      </div>
      <button class="btn-download" @click="handleDownload">下载app</button>
    </div>

    <!-- 可滚动主体 -->
    <div class="scroll-body">
      <p class="disclaimer">内容由AI生成,仅供参考</p>

      <div v-if="!loading && (!hasContent || error)" class="empty-state">
        <img :src="notFoundIconUrl" class="empty-icon" alt="" />
      </div>

      <!-- 问题卡片：灰框宽度 = 图片宽度与问题文字宽度取大，灰框包含图片+问题，无引号 -->
      <div v-else class="question-card-wrap">
        <div class="question-card" :class="{ 'has-image': firstImageUrl }">
          <div v-if="firstImageUrl" class="question-image-wrap">
            <img
              :src="firstImageUrl"
              class="question-image"
              alt=""
              @load="onImageLoad"
            />
          </div>
          <div class="question-text-wrap">
            <p class="question-text">{{ message.question || '暂无问题' }}</p>
          </div>
        </div>
      </div>

      <!-- 答案：图标同行开始输出答案，无"答案"二字 -->
      <div class="scroll-wrap" v-if="hasContent && !error">
        <div class="answer-section">
          <div class="answer-row">
            <img :src="answerIconUrl" class="answer-icon" alt="" />
            <div class="answer-content markdown-body" v-html="renderedAnswer"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 固定悬浮底部按钮，滑动不变位置 -->
    <div class="cta-fixed">
      <button class="btn-open" @click="handleOpenApp">打开app查看</button>
    </div>
  </div>
</template>

<script>
import appLogo from '@/assets/ai/app_logo.svg'
import answerIcon from '@/assets/ai/icon_answer.png'
import notFoundIcon from '@/assets/ai/not_found.svg'
import { openApp } from '@/utils/openApp'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

function preprocessMarkdown(text) {
  if (!text) return ''
  return text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(function (line) {
      // 去掉表格行和分隔线的前导空格，否则 marked 不认
      if (/^\s+\|/.test(line)) return line.replace(/^\s+/, '')
      if (/^\s+---/.test(line)) return line.replace(/^\s+/, '')
      return line
    })
    .join('\n')
}

export default {
  name: 'HomeFollowUp',
  props: {
    query: {
      type: Object,
      default: function () { return {} }
    }
  },
  data: function () {
    return {
      appLogo: appLogo,
      answerIconUrl: answerIcon,
      notFoundIconUrl: notFoundIcon,
      message: {
        question: '',
        answer: '',
        imageUrl: []
      },
      loading: false,
      error: null
    }
  },
  computed: {
    isRealFollowUp: function () {
      var page = (this.query && this.query.page) ? String(this.query.page).trim().toLowerCase() : ''
      return page === 'realfollowup' || page === '10'
    },
    apiUrl: function () {
      return this.isRealFollowUp ? '/share/getRealFollowUpMessage' : '/share/getFollowUpMessage'
    },
    id: function () {
      return this.query.id || (this.$route && this.$route.query && this.$route.query.id) || null
    },
    firstImageUrl: function () {
      var urls = this.message.imageUrl
      if (!Array.isArray(urls) || urls.length === 0) return ''
      return urls[0] || ''
    },
    hasContent: function () {
      var q = (this.message && this.message.question) ? String(this.message.question).trim() : ''
      var a = (this.message && this.message.answer) ? String(this.message.answer).trim() : ''
      return !!(q || a || this.firstImageUrl)
    },
    renderedAnswer: function () {
      var raw = this.message.answer || ''
      if (!raw.trim()) return '暂无答案'

      var cleaned = preprocessMarkdown(raw)

      marked.setOptions({
        gfm: true,
        breaks: true
      })

      var html = marked.parse(cleaned)
      return DOMPurify.sanitize(html)
    }
  },
  mounted() {
    this.fetchFollowUpMessage()
  },
  methods: {
    onImageLoad() {
      // 图片加载完成，如需可做占位或动画
    },
    async fetchFollowUpMessage() {
      if (!this.id) {
        this.error = '缺少分享ID参数'
        return
      }
      this.loading = true
      this.error = null
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL || ''}${this.apiUrl}?id=${this.id}`
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
        const result = await response.json()
        if (result.code === 200 && result.data) {
          const d = result.data
          this.message = {
            question: d.question || '',
            answer: d.answer || '',
            imageUrl: Array.isArray(d.imageUrl) ? d.imageUrl : (d.imageUrl ? [d.imageUrl] : [])
          }
        } else {
          this.error = result.message || '获取失败'
        }
      } catch (err) {
        console.error('获取FollowUp问答失败:', err)
        this.error = '网络异常'
      } finally {
        this.loading = false
      }
    },
    handleDownload() {
      openApp()
    },
    handleOpenApp() {
      openApp()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-ai-share {
  min-height: 100vh;
  background: #f6f6f6;
}

/* 固定顶部栏，滑动时不动 */
.header-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 20px;
  background: #fff;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
}

.brand-area {
  display: flex;
  align-items: center;
}

.brand-logo {
  width: 40px;
  height: 40px;
  margin-right: 10px;
  border-radius: 12px;
}

.brand-text {
  font-weight: 700;
  color: #f15b5b;
  font-size: 18px;
  letter-spacing: 0.2px;
}

.btn-download {
  height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: 12px;
  background: #ef5e5a;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

/* 可滚动主体：顶部留出固定栏，底部留出固定按钮 */
.scroll-body {
  padding-top: 56px;
  padding-bottom: calc(66px + 24px + env(safe-area-inset-bottom));
}

.disclaimer {
  text-align: center;
  color: #999;
  font-size: 13px;
  margin: 12px 0 16px;
  padding: 0 16px;
}

/* empty state */
.empty-state {
  min-height: calc(100vh - 56px - 66px - 24px - env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
}
.empty-icon {
  width: 148px;
  height: auto;
  display: block;
}

/* 灰框宽度 = 图片宽度与问题文字宽度取大，整体靠右展示 */
.question-card-wrap {
  display: flex;
  justify-content: flex-end;
  margin: 0 16px 16px;
}

.question-card {
  display: inline-flex;
  flex-direction: column;
  width: auto;
  max-width: 78%;
  padding: 20px;
  background: #f0f0f0;
  border-radius: 16px;
  overflow: hidden;
  box-sizing: border-box;
}

.question-image-wrap {
  margin-bottom: 12px;
}

.question-text-wrap {
  width: 100%;
  align-self: stretch;
}

.question-image {
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.question-text {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: #222;
  text-align: left;
  word-break: break-word;
}

.scroll-wrap {
  padding: 0 16px 24px;
}

.answer-section {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 图标与答案同一行开始输出，无"答案"二字 */
.answer-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.answer-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.answer-content {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  line-height: 1.7;
  color: #333;
  word-break: break-word;
}

/* markdown basic */
.markdown-body {
  width: 100%;
}

.markdown-body ::v-deep p {
  margin: 0 0 10px;
}
.markdown-body ::v-deep p:last-child {
  margin-bottom: 0;
}
.markdown-body ::v-deep h1,
.markdown-body ::v-deep h2,
.markdown-body ::v-deep h3,
.markdown-body ::v-deep h4,
.markdown-body ::v-deep h5,
.markdown-body ::v-deep h6 {
  margin: 12px 0 8px;
  font-weight: 600;
  line-height: 1.4;
}
.markdown-body ::v-deep h1 {
  font-size: 20px;
}
.markdown-body ::v-deep h2 {
  font-size: 18px;
}
.markdown-body ::v-deep h3 {
  font-size: 16px;
}
.markdown-body ::v-deep hr {
  margin: 14px 0;
  border: 0;
  border-top: 1px solid #e5e5e5;
}
.markdown-body ::v-deep ul,
.markdown-body ::v-deep ol {
  margin: 0 0 10px;
  padding-left: 18px;
}
.markdown-body ::v-deep li + li {
  margin-top: 4px;
}
.markdown-body ::v-deep strong,
.markdown-body ::v-deep b {
  font-weight: 600;
}
.markdown-body ::v-deep em {
  font-style: italic;
}
.markdown-body ::v-deep code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.92em;
  background: rgba(0, 0, 0, 0.05);
  padding: 0 4px;
  border-radius: 4px;
}
.markdown-body ::v-deep pre {
  margin: 10px 0;
  padding: 12px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.05);
  overflow: auto;
}
.markdown-body ::v-deep pre code {
  background: transparent;
  padding: 0;
}
.markdown-body ::v-deep table {
  width: 100%;
  margin: 12px 0;
  border-collapse: collapse;
  font-size: 14px;
}
.markdown-body ::v-deep thead {
  background: #fafafa;
}
.markdown-body ::v-deep th,
.markdown-body ::v-deep td {
  padding: 8px 6px;
  border: 1px solid #eee;
  text-align: left;
  vertical-align: top;
  word-break: break-word;
}
.markdown-body ::v-deep blockquote {
  margin: 10px 0;
  padding: 8px 12px;
  border-left: 3px solid #ffb3b0;
  background: #fff7f7;
  color: #555;
}

/* 固定悬浮底部按钮，无白色背景，直接覆盖在内容上 */
.cta-fixed {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 48px;
  z-index: 10;
  padding: 12px 20px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  background: transparent;
  pointer-events: none;
}

.btn-open {
  width: 100%;
  height: 66px;
  border: none;
  border-radius: 18px;
  background: #ef5e5a;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  pointer-events: auto;
  box-shadow: 0 4px 16px rgba(239, 94, 90, 0.4);
}
</style>
